import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animated, useSpring } from '@react-spring/web';
import pluralize from 'pluralize';

import Button from 'src/components/button';
import SwitchButton from 'src/components/switchButton';

import { AssessmentConfiguration, AssessmentParameter, Simulation } from 'src/types/coaching/simulation';

import { updateSimulationProperty } from 'src/redux/actions/coaching/simulation';
import { SimulationContext } from '../../context';

import _ from 'lodash';
import cx from 'classnames';
import styles from './assessmentConfiguration.module.scss';

interface AssessmentConfigurationsProps {
  simulation: Simulation;
}

interface AssessmentSettingsProps {
  parameters: AssessmentParameter[];
}

interface ParameterCardProps {
  parameter: AssessmentParameter;
}

interface ScoreCardProps {
  scorecard: AssessmentConfiguration;
  selected: boolean;
  onSelect: () => void;
  defaultScorecard: AssessmentConfiguration | null;
}

export const AssessmentConfigurations: React.FC<AssessmentConfigurationsProps> = ({ simulation }) => {

  const dispatch = useDispatch();
  const simulationContext = useContext(SimulationContext);

  const scorecards = useSelector((state: any) => {
    return _.get(state, "coaching.settings.scorecards.items", []);
  });

  let defaultScorecard =
    scorecards.find(s => !s.systemDefined && s.defaultConfiguration);

  if (!defaultScorecard) {
    defaultScorecard =
      scorecards.find(s => s.systemDefined && s.defaultConfiguration);
  }
 
  const activeScorecard = simulationContext.activeAssessmentScorecard || simulation.assessmentConfiguration || null;
  const setActiveScorecard = simulationContext.setActiveAssessmentScorecard;

  const activeConfig = useMemo(() => {
    return scorecards.find((c) => c.id === activeScorecard?.id) || null;
  }, [scorecards, activeScorecard]);

  const parameters = activeConfig?.parameters || [];

  const handleSelectScorecard = (scorecard: AssessmentConfiguration) => {
    if (setActiveScorecard) {
      setActiveScorecard(scorecard);
    }
  };

  const handleSave = () => {
    dispatch(
      updateSimulationProperty({
        simulation: simulation.id!,
        property: "assessmentConfigurationId",
        value: activeScorecard?.id!,
      }),
    );
    if(simulationContext.editAssessmentScoreCard) {
      simulationContext.editAssessmentScoreCard(false);
    }
  }

  const handleClose = () => {
    if(simulationContext.editAssessmentScoreCard) {
      simulationContext.editAssessmentScoreCard(false);
    }
  }

  const [springs, api] = useSpring(() => ({
    opacity: 1,
    transform: "translateY(0px)",
    config: { tension: 180, friction: 18 }
  }));

  useEffect(() => {
    if (!parameters) return;
  
    api.start({
      from: { opacity: 0, transform: "translateY(40px)" },
      to: {
        opacity: 1,
        transform: "translateY(0px)"
      },
      reset: true
    });
  }, [parameters]); 
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          {simulation.name}
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" className={cx(styles.action, styles.confirm)}
            icon="paperflite-tick" text="Confirm"
            onClick={handleSave} />
          <Button className={cx(styles.action, styles.cancel)}
            text="Cancel"
            onClick={handleClose}
          />
        </div>
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.left}>
          <div className={styles.heading}>select assessment framework</div>
          <div className={styles.subheading}>Learners in this simulation will be assessed and scored using the selected framework.
          </div>
          <div className={styles.list}>
            {scorecards.map((scorecard) => (
              <ScoreCard
                key={scorecard.id}
                scorecard={scorecard}
                selected={scorecard.id === activeScorecard?.id}
                onSelect={() => handleSelectScorecard(scorecard)}
                defaultScorecard={defaultScorecard}
              />
            ))}
          </div>
        </div>
        <div className={styles.right}>
          <animated.div style={springs}>
            <AssessmentSettings parameters={parameters} />
          </animated.div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentConfigurations;


const ScoreCard: React.FC<ScoreCardProps> = ({ scorecard, selected, onSelect, defaultScorecard }) => {
  const parameters = _.get(scorecard, 'parameters', []) as AssessmentParameter[];

  const isDefault = scorecard.id === defaultScorecard?.id;

  return ( 
    <div
      className={cx(styles.scoreCard, {
        [styles.active]: selected,
      })}
      onClick={onSelect}
    >
      <div className={styles.toggleRow}>
        <div className={styles.headerSection}>
          <div className={styles.name}>{scorecard?.name}</div>
          {isDefault && <div className={styles.defaultTag}>default</div>}
        </div>
        <SwitchButton
          checked={selected}
          id="default-scorecard"
          className={styles.switch}
        />
      </div>
      <div className={styles.metadata}>
        <div className={styles.statItem}>
          <div className={styles.value}>{`${parameters.length}`.padStart(2, "0")}</div>
          <div className={styles.label}> {pluralize("parameter", parameters.length)}</div>
        </div>
        <div className={styles.divider} />
        <div className={styles.statItem}>
          <div className={styles.value}>{`${parameters.reduce((sum, item) => sum + item.parameters.length, 0)}`.padStart(2, "0")}</div>
          <div className={styles.label}>{pluralize("sub-parameter", parameters.reduce((sum, item) => sum + item.parameters.length, 0))}</div>
        </div>
      </div>
    </div>
  )
}

export const AssessmentSettings: React.FC<AssessmentSettingsProps> = ({ parameters }) => {

  return (
    <div className={styles.parameterContainer}>
      <div className={styles.body}>
        <div className={styles.headers}>
          <div className={styles.header}>Parameters</div>
          <div className={styles.header}>Sub-parameters</div>
        </div>
        {parameters.map((parameter, index) => (
          <ParameterCard key={index}
            parameter={parameter}
          />
        ))}
      </div>
    </div>
  );
}

const ParameterCard: React.FC<ParameterCardProps> = ({ parameter }) => {
  return (
    <div className={styles.card}>
      <div className={styles.parameter}>
        <h6 className={styles.name}>{parameter.name}</h6>
        <div className={styles.weightage}>
          weightage for this assessment:
          <b className={styles.value}>{parameter.weightage}</b>
        </div>
      </div>
      <div className={styles.subParameters}>
        {parameter.parameters.map((subParameter) => (
          <div key={subParameter.uid} className={styles.subParameter}>
            <label className={styles.name}>{subParameter.name}</label>
            <div className={styles.value}>{subParameter.criteria}</div>
          </div>
        ))}
      </div>
    </div>
  );
}